import React, { useState, useEffect } from 'react';
import { employeeService } from '../../services/employeeService';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { supabase } from '../../lib/supabase';
import { Trash2, AlertTriangle, CheckCircle, XCircle, Link2, Link2Off, Shield, Eye, EyeOff, RefreshCw, Key } from 'lucide-react';

// ─── Toast notification ──────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white transition-all ${
    type === 'success' ? 'bg-green-600' : 'bg-red-600'
  }`}>
    {type === 'success' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
    <span className="text-sm font-medium">{message}</span>
    <button onClick={onClose} className="ml-2 text-white/80 hover:text-white">✕</button>
  </div>
);

// ─── Auth Badge ──────────────────────────────────────────────────────────────
const AuthBadge = ({ authUserId }) => {
  if (authUserId) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Link2 className="w-3 h-3" />
        Auth lié
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <Link2Off className="w-3 h-3" />
      Auth manquant
    </span>
  );
};

// ─── Password generator ──────────────────────────────────────────────────────
const generatePassword = () => {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  let pwd = '';
  for (let i = 0; i < 12; i++) {
    pwd += chars?.charAt(Math.floor(Math.random() * chars?.length));
  }
  return pwd;
};

const EmployeeManagementDashboard = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterZone, setFilterZone] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [toast, setToast] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Reset password state
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    role: 'chauffeur',
    zone: '',
    isActive: true,
    notes: '',
    // Auth fields
    password: '',
    confirmPassword: '',
    mustChangePassword: true,
    createAuthAccount: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [creatingAuth, setCreatingAuth] = useState(false);

  useEffect(() => {
    loadEmployees();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeeService?.getAll();
      setEmployees(data);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── Create Auth account via Edge Function ───────────────────────────────
  const createAuthAccount = async (email, password, name, role, employeeId, mustChangePassword) => {
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/create-employee-auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ email, password, name, role, employeeId, mustChangePassword }),
    });

    const result = await response?.json();
    if (!response?.ok || result?.error) {
      throw new Error(result?.error || 'Erreur lors de la création du compte Auth');
    }
    return result;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validate password fields for new employees with auth creation
    if (!editingEmployee && formData?.createAuthAccount) {
      if (!formData?.email?.trim()) {
        showToast('Un email est requis pour créer un compte de connexion', 'error');
        return;
      }
      if (!formData?.password) {
        showToast('Un mot de passe temporaire est requis', 'error');
        return;
      }
      if (formData?.password?.length < 8) {
        showToast('Le mot de passe doit contenir au moins 8 caractères', 'error');
        return;
      }
      if (formData?.password !== formData?.confirmPassword) {
        showToast('Les mots de passe ne correspondent pas', 'error');
        return;
      }
    }

    try {
      setCreatingAuth(true);

      if (editingEmployee) {
        await employeeService?.update(editingEmployee?.id, formData);
        showToast('Employé mis à jour avec succès');
      } else {
        // Create employee record first
        const newEmployee = await employeeService?.create({
          name: formData?.name,
          phone: formData?.phone,
          email: formData?.email,
          role: formData?.role,
          zone: formData?.zone,
          isActive: formData?.isActive,
          notes: formData?.notes,
          mustChangePassword: formData?.mustChangePassword,
        });

        // Create Auth account if requested
        if (formData?.createAuthAccount && formData?.email && formData?.password) {
          try {
            const authResult = await createAuthAccount(
              formData?.email,
              formData?.password,
              formData?.name,
              formData?.role,
              newEmployee?.id,
              formData?.mustChangePassword
            );
            showToast(
              authResult?.isExisting
                ? `✅ Employé créé et lié au compte Auth existant`
                : `✅ Employé créé avec compte de connexion`
            );
          } catch (authErr) {
            console.error('Auth creation error:', authErr);
            showToast(`Employé créé mais erreur Auth: ${authErr?.message}`, 'error');
          }
        } else {
          showToast('Employé créé avec succès (sans compte de connexion)');
        }
      }

      await loadEmployees();
      handleCloseModal();
    } catch (err) {
      setError(err?.message);
      showToast(err?.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setCreatingAuth(false);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee?.name,
      phone: employee?.phone || '',
      email: employee?.email || '',
      role: employee?.role,
      zone: employee?.zone || '',
      isActive: employee?.isActive,
      notes: employee?.notes || '',
      password: '',
      confirmPassword: '',
      mustChangePassword: true,
      createAuthAccount: false,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEmployee(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      role: 'chauffeur',
      zone: '',
      isActive: true,
      notes: '',
      password: '',
      confirmPassword: '',
      mustChangePassword: true,
      createAuthAccount: true,
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  // ─── Toggle active status directly ──────────────────────────────────────
  const handleToggleActive = async (employee) => {
    const newStatus = !employee?.isActive;
    try {
      setEmployees(prev => prev?.map(e =>
        e?.id === employee?.id ? { ...e, isActive: newStatus } : e
      ));
      await employeeService?.update(employee?.id, { isActive: newStatus });
      showToast(`${employee?.name} est maintenant ${newStatus ? 'actif' : 'inactif'}`);
    } catch (err) {
      setEmployees(prev => prev?.map(e =>
        e?.id === employee?.id ? { ...e, isActive: employee?.isActive } : e
      ));
      showToast(err?.message || 'Erreur lors du changement de statut', 'error');
    }
  };

  // ─── Reset password ──────────────────────────────────────────────────────
  const handleResetPassword = async (employee) => {
    if (!employee?.email) {
      showToast("Cet employé n'a pas d'email renseigné", 'error');
      return;
    }
    setResetTarget(employee);
    setResetting(true);
    try {
      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(employee?.email, {
        redirectTo: 'https://adtexpress.cloud/update-password',
      });
      if (resetError) throw resetError;

      // Mark must_change_password = true
      await supabase?.from('employees')?.update({ must_change_password: true })?.eq('id', employee?.id);

      showToast(`✅ Lien de réinitialisation envoyé à ${employee?.email}`);
      await loadEmployees();
    } catch (err) {
      showToast(err?.message || 'Erreur lors de la réinitialisation', 'error');
    } finally {
      setResetting(false);
      setResetTarget(null);
    }
  };

  // ─── Link auth user by email ─────────────────────────────────────────────
  const handleLinkAuth = async (employee) => {
    if (!employee?.email) {
      showToast("Cet employé n'a pas d'email renseigné", 'error');
      return;
    }
    try {
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('email', employee?.email?.toLowerCase())
        ?.single();

      const authId = profile?.id;

      if (!authId) {
        showToast(`Aucun compte Auth trouvé pour ${employee?.email}. Créez d'abord le compte dans Supabase Auth.`, 'error');
        return;
      }

      await employeeService?.update(employee?.id, { authUserId: authId });
      showToast(`✅ ${employee?.name} lié à Auth (${authId?.slice(0, 8)}…)`);
      await loadEmployees();
    } catch (err) {
      showToast(err?.message || 'Erreur lors de la liaison Auth', 'error');
    }
  };

  // ─── Delete employee with cascade ────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const empId = deleteTarget?.id;
      const empName = deleteTarget?.name;

      await supabase?.from('shipments')?.update({ driver_id: null })?.eq('driver_id', empId);
      await supabase?.from('pickup_tasks')?.update({ driver_id: null })?.eq('driver_id', empId);
      await supabase?.from('delivery_tasks')?.update({ driver_id: null })?.eq('driver_id', empId);
      try {
        await supabase?.from('fatou_reservations')?.update({ driver_id: null })?.eq('driver_id', empId);
      } catch (_) { /* column may not exist */ }

      const { error: deleteErr } = await supabase?.from('employees')?.delete()?.eq('id', empId);
      if (deleteErr) throw deleteErr;

      setEmployees(prev => prev?.filter(e => e?.id !== empId));
      setDeleteTarget(null);
      showToast(`${empName} a été supprimé avec succès`);
    } catch (err) {
      console.error('Error deleting employee:', err);
      setDeleteError(err?.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const filteredEmployees = employees?.filter(emp => {
    const matchesSearch = emp?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          (emp?.email && emp?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())) ||
                          (emp?.phone && emp?.phone?.includes(searchTerm));
    const matchesRole = filterRole === 'all' || emp?.role === filterRole;
    const matchesZone = filterZone === 'all' || emp?.zone === filterZone;
    const matchesActive = filterActive === 'all' ||
                         (filterActive === 'active' && emp?.isActive) ||
                         (filterActive === 'inactive' && !emp?.isActive);
    const matchesAuth = filterRole === 'auth_linked' ? !!emp?.authUserId :
                        filterRole === 'auth_missing' ? !emp?.authUserId : true;
    return matchesSearch && matchesRole && matchesZone && matchesActive && matchesAuth;
  });

  const zones = [...new Set(employees?.map(e => e?.zone)?.filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des employés...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Employés / Chauffeurs</h1>
          <p className="mt-2 text-sm text-gray-600">
            Gérez les chauffeurs et le personnel — liaison Supabase Auth visible via le badge Auth lié/manquant
          </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input
              type="text"
              placeholder="Rechercher par nom, email ou téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Tous les rôles</option>
              <option value="chauffeur">Chauffeur</option>
              <option value="aide">Aide</option>
              <option value="admin">Admin</option>
              <option value="auth_linked">✅ Auth lié</option>
              <option value="auth_missing">❌ Auth manquant</option>
            </select>

            <select
              value={filterZone}
              onChange={(e) => setFilterZone(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Toutes les zones</option>
              {zones?.map(zone => (
                <option key={zone} value={zone}>{zone}</option>
              ))}
            </select>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e?.target?.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>

            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              + Ajouter Employé
            </button>
          </div>
        </div>

        {/* Employees Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom / Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auth</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MDP requis</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEmployees?.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                      Aucun employé trouvé
                    </td>
                  </tr>
                ) : (
                  filteredEmployees?.map((employee) => (
                    <tr key={employee?.id} className="hover:bg-gray-50">
                      {/* Nom / Email */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{employee?.name}</div>
                        {employee?.email && (
                          <div className="text-xs text-gray-500">{employee?.email}</div>
                        )}
                        {employee?.authUserId && (
                          <div className="text-xs font-mono text-gray-400 mt-0.5" title={employee?.authUserId}>
                            {employee?.authUserId?.slice(0, 8)}…
                          </div>
                        )}
                      </td>

                      {/* Téléphone */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee?.phone || '-'}
                      </td>

                      {/* Rôle */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          employee?.role === 'chauffeur' ? 'bg-blue-100 text-blue-800' :
                          employee?.role === 'aide' ? 'bg-purple-100 text-purple-800' :
                          employee?.role === 'admin'? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {employee?.role}
                        </span>
                      </td>

                      {/* Zone */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {employee?.zone || '-'}
                      </td>

                      {/* Statut — cliquable */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(employee)}
                          title={`Cliquer pour ${employee?.isActive ? 'désactiver' : 'activer'}`}
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer transition-all hover:opacity-80 ${
                            employee?.isActive ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {employee?.isActive ? '✓ Actif' : '✗ Inactif'}
                        </button>
                      </td>

                      {/* Auth Badge */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <AuthBadge authUserId={employee?.authUserId} />
                      </td>

                      {/* Must change password */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {employee?.mustChangePassword ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <Key className="w-3 h-3" />
                            Requis
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleEdit(employee)}
                            className="text-green-600 hover:text-green-900 font-medium text-xs"
                          >
                            Modifier
                          </button>
                          {employee?.email && (
                            <button
                              onClick={() => handleResetPassword(employee)}
                              disabled={resetting && resetTarget?.id === employee?.id}
                              className="text-blue-600 hover:text-blue-900 font-medium text-xs flex items-center gap-1"
                              title="Envoyer lien de réinitialisation"
                            >
                              <RefreshCw className="w-3 h-3" />
                              Réinit. MDP
                            </button>
                          )}
                          {!employee?.authUserId && employee?.email && (
                            <button
                              onClick={() => handleLinkAuth(employee)}
                              className="text-purple-600 hover:text-purple-900 font-medium text-xs flex items-center gap-1"
                              title="Lier au compte Supabase Auth"
                            >
                              <Link2 className="w-3 h-3" />
                              Lier Auth
                            </button>
                          )}
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => { setDeleteTarget(employee); setDeleteError(''); }}
                            className="text-red-500 hover:text-red-700 font-medium flex items-center gap-1 text-xs"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700">
              <strong>Liaison Auth :</strong> Le badge <span className="bg-green-100 text-green-800 px-1 rounded">Auth lié</span> signifie que l'employé est relié à un compte Supabase Auth. 
              Le badge <span className="bg-red-100 text-red-800 px-1 rounded">Auth manquant</span> signifie que la connexion est impossible. 
              Utilisez <strong>Lier Auth</strong> pour relier automatiquement par email, ou consultez <strong>Administration &gt; Diagnostic Utilisateurs</strong>.
              <br />
              <strong>Réinit. MDP :</strong> Envoie un lien de réinitialisation Supabase vers <code>https://adtexpress.cloud/update-password</code>.
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 my-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {editingEmployee ? 'Modifier Employé' : 'Nouvel Employé'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  required
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email {!editingEmployee && <span className="text-red-500">*</span>}</label>
                <input
                  type="email"
                  value={formData?.email}
                  onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select
                  value={formData?.role}
                  onChange={(e) => setFormData({ ...formData, role: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="chauffeur">Chauffeur</option>
                  <option value="aide">Aide</option>
                  <option value="admin">Admin</option>
                  <option value="agent">Agent</option>
                  <option value="agent_abidjan">Agent Abidjan</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zone</label>
                <input
                  type="text"
                  value={formData?.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e?.target?.value })}
                  placeholder="Ex: Nord, Sud, Evry"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e?.target?.checked })}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">
                    Actif {formData?.isActive ? '(accès autorisé)' : '(accès bloqué)'}
                  </span>
                </label>
              </div>

              {/* Auth section — only for new employees */}
              {!editingEmployee && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-green-600" />
                      Compte de connexion Supabase Auth
                    </h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData?.createAuthAccount}
                        onChange={(e) => setFormData({ ...formData, createAuthAccount: e?.target?.checked })}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-xs text-gray-600">Créer le compte</span>
                    </label>
                  </div>

                  {formData?.createAuthAccount && (
                    <div className="space-y-3 bg-green-50 rounded-lg p-3">
                      {/* Password */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Mot de passe temporaire *
                        </label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={formData?.password}
                              onChange={(e) => setFormData({ ...formData, password: e?.target?.value })}
                              placeholder="Minimum 8 caractères"
                              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              tabIndex={-1}
                            >
                              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              let pwd = generatePassword();
                              setFormData({ ...formData, password: pwd, confirmPassword: pwd });
                              setShowPassword(true);
                            }}
                            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap"
                            title="Générer un mot de passe"
                          >
                            <RefreshCw size={12} />
                            Générer
                          </button>
                        </div>
                      </div>

                      {/* Confirm password */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Confirmer le mot de passe *
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={formData?.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e?.target?.value })}
                            placeholder="Répétez le mot de passe"
                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                        {formData?.confirmPassword && (
                          <p className={`text-xs mt-1 ${formData?.password === formData?.confirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                            {formData?.password === formData?.confirmPassword ? '✓ Correspondent' : '✗ Ne correspondent pas'}
                          </p>
                        )}
                      </div>

                      {/* Must change password */}
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData?.mustChangePassword}
                          onChange={(e) => setFormData({ ...formData, mustChangePassword: e?.target?.checked })}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-xs text-gray-700">
                          Forcer le changement du mot de passe à la première connexion
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creatingAuth}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {creatingAuth ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Création...
                    </>
                  ) : (
                    editingEmployee ? 'Mettre à jour' : 'Créer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Supprimer l'employé</h3>
                <p className="text-sm text-gray-500">{deleteTarget?.name}</p>
              </div>
            </div>

            <p className="text-gray-700 mb-2">
              Voulez-vous vraiment supprimer cet employé ? Il perdra tous ses accès au logiciel.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-amber-800">
                <strong>Note :</strong> L'historique des collectes et livraisons passées sera conservé.
                Les affectations futures seront supprimées. Le compte de connexion sera désactivé.
              </p>
            </div>

            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {deleteError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => { setDeleteTarget(null); setDeleteError(''); }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Supprimer définitivement
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

export default EmployeeManagementDashboard;